// Reusable form primitives for the admin pages — Field wrapper plus
// TextInput / NumberInput / Select / TextArea / Banner. Pulled out of
// `pages/CardEditorPage.tsx` so future admin forms (and the existing
// NPC-deck flows down the road) don't each re-roll their own input
// styling. Kept deliberately minimal — these are admin-side controls,
// not the play UI's components, so they share the pixelTheme look
// without dragging in the heavier `panelStyle` chrome.

import type { ReactNode } from "react";
import type { Theme } from "../../theme/pixel";

export function Field({
  theme,
  label,
  hint,
  children,
}: {
  theme: Theme;
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <label
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 10,
            letterSpacing: theme.letterSpacing,
            color: theme.inkFaint,
          }}
        >
          {label.toUpperCase()}
        </label>
        {hint && <span style={{ fontSize: 11, color: theme.inkDim }}>{hint}</span>}
      </div>
      {children}
    </div>
  );
}

export function TextInput({
  theme,
  value,
  onChange,
  placeholder,
  disabled = false,
}: {
  theme: Theme;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      style={{
        width: "100%",
        boxSizing: "border-box",
        padding: "8px 10px",
        fontFamily: theme.fontBody,
        fontSize: 14,
        background: disabled ? theme.panel : theme.panelSolid,
        color: disabled ? theme.inkFaint : theme.ink,
        border: `2px solid ${theme.panelEdge}`,
      }}
    />
  );
}

export function NumberInput({
  theme,
  value,
  onChange,
}: {
  theme: Theme;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <input
      type="number"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: "100%",
        boxSizing: "border-box",
        padding: "8px 10px",
        fontFamily: theme.fontBody,
        fontSize: 14,
        background: theme.panelSolid,
        color: theme.ink,
        border: `2px solid ${theme.panelEdge}`,
      }}
    />
  );
}

export function Select({
  theme,
  value,
  onChange,
  options,
}: {
  theme: Theme;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: "100%",
        boxSizing: "border-box",
        padding: "8px 10px",
        fontFamily: theme.fontBody,
        fontSize: 14,
        background: theme.panelSolid,
        color: theme.ink,
        border: `2px solid ${theme.panelEdge}`,
      }}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function TextArea({
  theme,
  value,
  onChange,
  rows = 3,
  placeholder,
  monospace = false,
}: {
  theme: Theme;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  placeholder?: string;
  monospace?: boolean;
}) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      placeholder={placeholder}
      style={{
        width: "100%",
        boxSizing: "border-box",
        padding: "8px 10px",
        fontFamily: monospace ? "ui-monospace, monospace" : theme.fontBody,
        fontSize: monospace ? 12 : 14,
        background: theme.panelSolid,
        color: theme.ink,
        border: `2px solid ${theme.panelEdge}`,
        resize: "vertical",
      }}
    />
  );
}

export function Banner({
  theme,
  kind,
  children,
}: {
  theme: Theme;
  kind: "error" | "info";
  children: ReactNode;
}) {
  const color = kind === "error" ? theme.accent : theme.ink;
  return (
    <div
      style={{
        padding: 10,
        background: theme.panel,
        border: `2px solid ${kind === "error" ? theme.accent : theme.panelEdge}`,
        color,
        fontSize: 13,
      }}
    >
      {children}
    </div>
  );
}
